import { commonComponents as cc } from "@/components/common";
import TextForm from "@/components/form/TextForm";
import { ui } from "@/components/ui";
import EditInjectionAsPatient from "@/features/injection/components/EditInjectionAsPatient";
import type { StatefulTrackerViewT, Tracker } from "@/types/database/trackers";
import { Fragment } from "react";
import type { StatefulDiaryT } from "../../../../../types/database/diaries";
import ConsentCall from "../../../../consent/ConsentCall";
import SharedAttributes from "../../../../shared_attributes/components/SharedAttributes";
import { useUpdateTracker } from "../../../../trackers/api/updateTracker";
import useUpdateDiary from "../api/updateDiary";

type UpdateTrackerT = Tracker["Update"] & { tracker_id: string };

const EditDiary = ({ diary }: { diary: StatefulDiaryT }) => {
	const { mutateAsync: mutateDiary } = useUpdateDiary();
	const { mutateAsync: mutateTracker } = useUpdateTracker({
		diaryId: diary.diary_id,
	});

	const handleUpdateDiary = async (updates: {
		diary_name?: string | undefined;
	}) => {
		await mutateDiary({
			diary_id: diary.diary_id,
			...updates,
		});
	};

	const handleUpdateTracker = async (
		trackerId: string,
		updates: Partial<UpdateTrackerT>,
	) => {
		await mutateTracker({
			...updates,
			tracker_id: trackerId,
		});
	};

	return (
		<section className="space-y-4">
			<ConsentCall consents={diary?.consents_view} />
			<cc.Group title="General">
				<cc.EditDialog
					label="Diary Name"
					description="The name of this diary."
					info={diary.diary_name}
					noInfo={!diary.diary_name}
				>
					<TextForm
						label="Diary Name"
						initialValue={diary.diary_name || ""}
						onSubmit={(value) =>
							handleUpdateDiary({ diary_name: value as string })
						}
						submitButtonText="Save Name"
					/>
				</cc.EditDialog>

				<SharedAttributes
					object={diary}
					organisationId={diary.organisation_id || ""}
				/>
			</cc.Group>

			{diary.trackers_view?.map((tracker: StatefulTrackerViewT, i: number) => {
				const hasAttributes = !!tracker.shared_attributes?.shared_attribute_id;

				return (
					<cc.Group
						key={tracker.tracker_id}
						title={`Tracker Section #${i + 1}`}
					>
						<cc.EditDialog
							label="Tracker Name"
							description="The name of this specific tracker."
							info={tracker.tracker_name}
							noInfo={!tracker.tracker_name}
						>
							<TextForm
								label="Tracker Name"
								initialValue={tracker.tracker_name ?? ""}
								onSubmit={(value) =>
									handleUpdateTracker(tracker.tracker_id, {
										tracker_name: value,
									})
								}
								submitButtonText="Save Name"
							/>
						</cc.EditDialog>

						{hasAttributes && (
							<ui.Accordion type="single" collapsible>
								<ui.AccordionItem value="tracker-settings">
									<ui.AccordionTrigger>Tracker Settings</ui.AccordionTrigger>
									<ui.AccordionContent>
										<div className="space-y-4">
											<SharedAttributes
												object={tracker}
												organisationId={diary.organisation_id || ""}
											/>
										</div>
									</ui.AccordionContent>
								</ui.AccordionItem>
							</ui.Accordion>
						)}
					</cc.Group>
				);
			})}

			<cc.Group title="Events">
				{diary.events_view?.map((event) => (
					<Fragment key={event.event_id || `event-${Math.random()}`}>
						{event.injections_view?.map((injection) => (
							<EditInjectionAsPatient
								key={injection.injection_id}
								injection={injection}
								diaryId={diary.diary_id}
							/>
						))}
					</Fragment>
				))}
			</cc.Group>
		</section>
	);
};

export default EditDiary;
